import type { CSSProperties } from 'react';

import Link from 'next/link';

import { cn } from '@ps/ui/utils';

import type { MainActionButtonViewModel } from '../types/MainActionButtonViewModel';
import {
  MainActionButtonContent,
  kMainActionButtonClassName,
  mainActionButtonInlineStyle,
} from './MainActionButtonContent';

interface Props {
  button: MainActionButtonViewModel;
  backgroundColor: string;
  textColor: string;
  /** Fully-resolved label style (font family/size from the adapter) — applied
   *  inline so the visitor button needs no font-config context. */
  textStyle: CSSProperties;
  className?: string;
}

/**
 * Visitor main action button — a `next/link`. Stays SERVER-rendered (no
 * `'use client'`) and emits a crawlable `<a href>`, but navigates CLIENT-SIDE:
 * every main-action target is an internal bio-scoped route (`/<url_code>/...`),
 * so the soft transition is faster than a full reload AND lets the bio's
 * `@modal/(.)*` intercepts fire — e.g. Subscribe overlays as a modal on the live
 * bio instead of hard-loading a fresh page. `next/link` is already in the shared
 * runtime (used across the bio), so this adds no bundle weight. The icon comes
 * from `button.type` unless overridden by `button.leftIcon`; `flex-1` shares the
 * row evenly via the row's `gap`. The `dm` button uses a client island
 * ({@link MessageMeButton}) instead, since it gates on login/subscription.
 */
export function MainActionLink({
  button,
  backgroundColor,
  textColor,
  textStyle,
  className,
}: Props) {
  return (
    <Link
      href={button.href}
      className={cn(kMainActionButtonClassName, className)}
      style={mainActionButtonInlineStyle({
        backgroundColor,
        textColor,
        textStyle,
      })}
    >
      <MainActionButtonContent
        type={button.type}
        label={button.label}
        labelNode={button.labelNode}
        textColor={textColor}
        leftIcon={button.leftIcon}
      />
    </Link>
  );
}
